import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../compos/Header/Header';
import Footer from '../../compos/Footer/Footer';
import { useUser } from '../../UserContext';
import { useData } from '../../DataContext';
import { BACKEND_URL } from '../../config';

const Signup = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, set_user, setIsAdmin, setIsLoggedIn, getPostId, isAuthLoading } = useUser();
	const { clearData } = useData();

	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [phoneNumber, setPhoneNumber] = useState('');
	const [otp, setOtp] = useState('');
	const [name, setName] = useState('');
	const [password, setPassword] = useState('');
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);

	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		}, 10);
	}, []);

	if (isAuthLoading) {
		return (
			<div className='flex justify-center items-center h-screen'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#022c56]'></div>
			</div>
		);
	}

	if (getPostId !== '' && user != null) {
		return <Navigate to={`/getpost/${getPostId}`} />;
	} else if (user != null) {
		const destination = location.state?.from || '/reports';
		return <Navigate to={destination} />;
	}

	const handleRequestOTP = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!phoneNumber || phoneNumber.length !== 10) {
			setError('Phone number must be exactly 10 digits.');
			return;
		}

		setIsLoading(true);
		setError(null);
		try {
			const response = await fetch(`${BACKEND_URL}/auth/generateOTP`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ phoneNumber: '91' + phoneNumber, purpose: 'signUp' })
			});
			const data = await response.json();
			
			if (response.ok) {
				setStep(2);
			} else {
				setError(data.message || 'Failed to request OTP. Please try again.');
			}
		} catch (err) {
			setError('Network error. Please check your connection.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOTP = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!otp || otp.length < 6) {
			setError('Please enter a valid OTP.');
			return;
		}

		setIsLoading(true);
		setError(null);
		try {
			const response = await fetch(`${BACKEND_URL}/auth/confirmOTP`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ OTP: otp })
			});
			
			if (response.ok) {
				setStep(3);
			} else {
				const data = await response.json();
				setError(data.message || 'Invalid OTP. Please try again.');
			}
		} catch (err) {
			setError('Network error. Please check your connection.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSignup = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) {
			setError('Name is required.');
			return;
		}
		if (!password || password.length < 8) {
			setError('Password must be at least 8 characters.');
			return;
		}

		setIsLoading(true);
		setError(null);
		try {
			const response = await fetch(`${BACKEND_URL}/auth/signUp`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ 
					phoneNumber: '91' + phoneNumber,
					name,
					password
				})
			});

			if (response.ok) {
				// Signup successful, immediately login the user
				await performLogin('91' + phoneNumber, password);
			} else {
				const data = await response.json();
				setError(data.message || 'Signup failed. Please try again.');
				setIsLoading(false);
			}
		} catch (err) {
			setError('Network error. Please check your connection.');
			setIsLoading(false);
		}
	};

	const performLogin = async (fullPhone: string, pass: string) => {
		var urlencoded = new URLSearchParams();
		urlencoded.append('phoneNumber', fullPhone);
		urlencoded.append('password', pass);

		try {
			const loginResponse = await fetch(`${BACKEND_URL}/auth/loginWeb`, {
				method: 'POST',
				body: urlencoded,
				credentials: 'include',
			});

			if (loginResponse.ok) {
				localStorage.removeItem('authToken');
				localStorage.setItem('isAuthed', '1');
				clearData();

				// Fetch Profile
				const profileRes = await fetch(`${BACKEND_URL}/user/myProfile`, {
					method: 'GET',
					credentials: 'include',
				});

				if (profileRes.ok) {
					const result = await profileRes.json();
					if (result.id) {
						set_user(result);
						setIsLoggedIn(true);
						await checkIfAdmin(setIsAdmin);
						// Navigate will be handled by the user != null check at the top
					}
				}
			} else {
				setError('Signup successful, but login failed. Please go to Login page.');
			}
		} catch (err) {
			setError('Network error during login. Please proceed to login manually.');
		} finally {
			setIsLoading(false);
		}
	};

	const checkIfAdmin = async (setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>) => {
		try {
			const response = await fetch(`${BACKEND_URL}/api/isAdmin`, {
				method: 'GET',
				credentials: 'include',
			});
			if (response.ok) {
				const data = await response.json();
				setIsAdmin(data);
			} else {
				setIsAdmin(false);
			}
		} catch (error) {
			setIsAdmin(false);
		}
	};

	return (
		<div className='min-h-screen bg-slate-50 flex flex-col justify-between'>
			<Header />
			
			<div className='flex-grow flex items-center justify-center p-4 mt-20 mb-10'>
				<div className='bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] max-w-md w-full p-8 border border-slate-100 relative overflow-hidden'>
					<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
					
					<h2 className='text-3xl font-extrabold text-slate-900 mb-2'>Create Account</h2>
					
					{/* Progress bar */}
					<div className="flex items-center space-x-2 mb-8 mt-6">
						<div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
						<div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
						<div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
					</div>

					{error && (
						<div className='mb-6 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm font-medium'>
							{error}
						</div>
					)}

					{/* Step 1: Phone Number */}
					{step === 1 && (
						<form onSubmit={handleRequestOTP} className="space-y-5 animate-in fade-in zoom-in-95 duration-300">
							<p className='text-slate-500 text-sm mb-4'>Enter your phone number to receive a verification code.</p>
							
							<div>
								<label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number</label>
								<div className="flex border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
									<span className="bg-slate-50 px-4 py-3 text-slate-500 border-r border-slate-300 font-medium">+91</span>
									<input
										type="text"
										value={phoneNumber}
										onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
										className="w-full px-4 py-3 outline-none"
										placeholder="10-digit number"
										required
										autoFocus
									/>
								</div>
							</div>

							<button
								type="submit"
								disabled={isLoading || phoneNumber.length !== 10}
								className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 disabled:opacity-70 transition-all"
							>
								{isLoading ? 'Sending...' : 'Send OTP'}
							</button>
						</form>
					)}

					{/* Step 2: OTP */}
					{step === 2 && (
						<form onSubmit={handleVerifyOTP} className="space-y-5 animate-in slide-in-from-right-4 duration-300">
							<p className='text-slate-500 text-sm mb-4'>
								We sent a verification code to <strong className="text-slate-800">+91 {phoneNumber}</strong>.
								<button type="button" onClick={() => setStep(1)} className="ml-2 text-blue-600 hover:underline">Change</button>
							</p>
							
							<div>
								<label className="block text-sm font-semibold text-slate-700 mb-1">Verification Code (OTP)</label>
								<input
									type="text"
									value={otp}
									onChange={(e) => setOtp(e.target.value)}
									className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all tracking-widest text-lg font-medium"
									placeholder="XXXXXX"
									required
									maxLength={6}
									autoFocus
								/>
							</div>

							<button
								type="submit"
								disabled={isLoading || !otp}
								className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 disabled:opacity-70 transition-all"
							>
								{isLoading ? 'Verifying...' : 'Verify OTP'}
							</button>
						</form>
					)}

					{/* Step 3: Name & Password */}
					{step === 3 && (
						<form onSubmit={handleSignup} className="space-y-5 animate-in slide-in-from-right-4 duration-300">
							<p className='text-slate-500 text-sm mb-4'>Almost done! Please provide your name and create a password.</p>
							
							<div>
								<label className="block text-sm font-semibold text-slate-700 mb-1">Full Name</label>
								<input
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
									placeholder="e.g. John Doe"
									required
									autoFocus
								/>
							</div>

							<div>
								<label className="block text-sm font-semibold text-slate-700 mb-1">Create Password</label>
								<div className="relative border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all bg-white">
									<input
										type={isPasswordVisible ? "text" : "password"}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className="w-full px-4 py-3 pr-12 outline-none"
										placeholder="At least 8 characters"
										required
										minLength={8}
									/>
									<button
										type="button"
										onClick={() => setIsPasswordVisible(!isPasswordVisible)}
										className="absolute right-0 top-0 h-full px-4 text-slate-400 hover:text-blue-600 transition-colors focus:outline-none"
									>
										<i className={`bi ${isPasswordVisible ? 'bi-eye' : 'bi-eye-slash'} text-lg`}></i>
									</button>
								</div>
							</div>

							<button
								type="submit"
								disabled={isLoading || !name || password.length < 8}
								className="w-full py-4 bg-[#022c56] text-white rounded-xl font-bold shadow-lg hover:bg-[#031d38] disabled:opacity-70 transition-all"
							>
								{isLoading ? 'Creating Account...' : 'Complete Signup'}
							</button>
						</form>
					)}
					
					<div className="mt-8 pt-6 border-t border-slate-100 text-center">
						<p className="text-sm text-slate-600">
							Already have an account? <button onClick={() => navigate('/login', { state: location.state })} className="text-blue-600 font-semibold hover:underline">Log in</button>
						</p>
					</div>

				</div>
			</div>

			<Footer />
		</div>
	);
};

export default Signup;
